import NiceModal from "@ebay/nice-modal-react";
import { ThemeProvider } from "@frontend/ui-kit/ThemeProvider";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { canvClass } from "@/canv/canv";
import { canv, setCanv } from "@/canv/global";
import { type EventHandler, eventEmitter } from "@/eventEmitter";
import styles from "./App.module.css";
import {
    activeVoteVisibilityAtom,
    boardInfoAtom,
    buttonDisabledAtom,
    currentToolAtom,
    headerActiveButtonAtom,
    multiCursorsAtom,
    searchResultAtom,
    startVoteActiveTabAtom,
    startVoteVisibilityAtom,
    usersAtom,
    voteTimerVisibilityAtom,
    zoomAtom,
} from "./AppStore";
import { BottomBar } from "./BottomBar";
import { HeaderBar } from "./HeaderBar";

type AppProps = {
    boardData: Record<string, unknown>;
};

export const App = ({ boardData }: AppProps) => {
    if (!canv) {
        const c = new canvClass();
        setCanv(c);
        c.startApp(boardData);
    }

    const setZoom = useSetAtom(zoomAtom);
    const setCurrentTool = useSetAtom(currentToolAtom);
    const setSearchResults = useSetAtom(searchResultAtom);
    const setBoardInfo = useSetAtom(boardInfoAtom);
    const setMultiCursors = useSetAtom(multiCursorsAtom);
    const setButtonDisabled = useSetAtom(buttonDisabledAtom);
    const setUsers = useSetAtom(usersAtom);
    const setVoteTimerVisibility = useSetAtom(voteTimerVisibilityAtom);
    const setHeaderActiveButtonName = useSetAtom(headerActiveButtonAtom);
    const setStartVoteVisibility = useSetAtom(startVoteVisibilityAtom);
    const setStartVoteActiveTab = useSetAtom(startVoteActiveTabAtom);
    const setActiveVoteVisibility = useSetAtom(activeVoteVisibilityAtom);

    useEffect(() => {
        const handleZoomUpdate: EventHandler<"canvas.zoom.update"> = ({ zoom }) => {
            setZoom(zoom);
        };
        const handleToolSwitch: EventHandler<"toolbox.switch"> = ({ toolName }) => {
            setCurrentTool(toolName);
        };
        const handleSearchResultsUpdate: EventHandler<"search.results.update"> = (data) => {
            setSearchResults(data);
        };
        const handleBoardInfoChange: EventHandler<"board.info"> = (boardInfo) => {
            setBoardInfo(boardInfo);
        };
        const handleMultiCursorsChange: EventHandler<"multi.cursors"> = ({
            disableMultiCursors,
        }) => {
            setMultiCursors({
                disableMultiCursors,
            });
        };
        const handleButtonDisabled: EventHandler<"button.disabled"> = (buttonStates) => {
            setButtonDisabled((prev) => ({
                ...prev,
                ...buttonStates,
            }));
        };
        const handleUserSessionsChange: EventHandler<"multi.users.sessions"> = ({ users }) => {
            setUsers((prev) => ({
                ...prev,
                users: users,
            }));
        };
        const handleUserTrackingSessionsChange: EventHandler<"multi.users.trackingSession"> = ({
            session,
        }) => {
            setUsers((prev) => ({
                ...prev,
                trackingSession: session,
            }));
        };
        const handleVoteTimerVisibilityChange: EventHandler<"vote.timer.visibility"> = ({
            isVisible,
        }) => {
            setVoteTimerVisibility(isVisible);
        };
        const handleHeaderActiveButtonNameChange: EventHandler<"header.button.active.name"> = ({
            name,
        }) => {
            setHeaderActiveButtonName(name);
        };
        const handlestartVoteVisibilityChange: EventHandler<"vote.start.visibility"> = ({
            isVisible,
        }) => {
            setStartVoteVisibility(isVisible);
        };
        const handleStartVoteActiveTabChange: EventHandler<"vote.start.activeTab"> = ({ tab }) => {
            setStartVoteActiveTab(tab);
        };
        const handleActiveVoteVisibilityChange: EventHandler<"vote.active.visibility"> = ({
            isVisible,
        }) => {
            setActiveVoteVisibility(isVisible);
        };
        eventEmitter.on("canvas.zoom.update", handleZoomUpdate);
        eventEmitter.on("toolbox.switch", handleToolSwitch);
        eventEmitter.on("search.results.update", handleSearchResultsUpdate);
        eventEmitter.on("board.info", handleBoardInfoChange);
        eventEmitter.on("multi.cursors", handleMultiCursorsChange);
        eventEmitter.on("button.disabled", handleButtonDisabled);
        eventEmitter.on("multi.users.sessions", handleUserSessionsChange);
        eventEmitter.on("multi.users.trackingSession", handleUserTrackingSessionsChange);
        eventEmitter.on("vote.timer.visibility", handleVoteTimerVisibilityChange);
        eventEmitter.on("header.button.active.name", handleHeaderActiveButtonNameChange);
        eventEmitter.on("vote.start.visibility", handlestartVoteVisibilityChange);
        eventEmitter.on("vote.start.activeTab", handleStartVoteActiveTabChange);
        eventEmitter.on("vote.active.visibility", handleActiveVoteVisibilityChange);

        setBoardInfo(canv.initData);
        setMultiCursors({
            disableMultiCursors: canv.multiManager.disableMultiCursors,
        });

        return () => {
            eventEmitter.off("canvas.zoom.update", handleZoomUpdate);
            eventEmitter.off("toolbox.switch", handleToolSwitch);
            eventEmitter.off("search.results.update", handleSearchResultsUpdate);
            eventEmitter.off("board.info", handleBoardInfoChange);
            eventEmitter.off("multi.cursors", handleMultiCursorsChange);
            eventEmitter.off("button.disabled", handleButtonDisabled);
            eventEmitter.off("multi.users.sessions", handleUserSessionsChange);
            eventEmitter.off("multi.users.trackingSession", handleUserTrackingSessionsChange);
            eventEmitter.off("vote.timer.visibility", handleVoteTimerVisibilityChange);
            eventEmitter.off("header.button.active.name", handleHeaderActiveButtonNameChange);
            eventEmitter.off("vote.start.visibility", handlestartVoteVisibilityChange);
            eventEmitter.off("vote.start.activeTab", handleStartVoteActiveTabChange);
            eventEmitter.off("vote.active.visibility", handleActiveVoteVisibilityChange);
        };
    }, [
        setZoom,
        setCurrentTool,
        setSearchResults,
        setBoardInfo,
        setMultiCursors,
        setButtonDisabled,
        setUsers,
        setVoteTimerVisibility,
        setHeaderActiveButtonName,
        setStartVoteVisibility,
        setStartVoteActiveTab,
        setActiveVoteVisibility,
    ]);

    return (
        <ThemeProvider theme="light">
            <NiceModal.Provider>
                <HeaderBar />
                <BottomBar />
            </NiceModal.Provider>
        </ThemeProvider>
    );
};
